function effectCellId(draft, effect) {
    if (effect.scope.kind === 'cell')
        return effect.scope.cellId;
    if (effect.scope.kind === 'unit')
        return draft.instances[effect.scope.instanceId]?.cellId;
    return undefined;
}
/** Экземпляр «жив» = на поле и реально занимает свою ячейку (tech/22 §3). */
export function isLiveUnit(draft, inst) {
    if (inst.zone !== 'board' || inst.cellId === undefined)
        return false;
    const cell = draft.board.cells[inst.cellId];
    return cell !== undefined && cell.occupantId === inst.id;
}
function hookDefined(hooks, name) {
    return typeof hooks?.[name] === 'function';
}
/** Собрать подписчиков события (до сортировки). */
export function collectSubscribers(draft, registry, event) {
    // Финальный маркер матча не реактивен: победитель уже зафиксирован, а событие
    // обязано оставаться последним в журнале резолюции.
    if (event.type === 'MatchEnded')
        return [];
    if (event.type === 'CardPlanned' ||
        event.type === 'CardUnplanned' ||
        event.type === 'PlacementStaged' ||
        event.type === 'PlacementUnstaged' ||
        event.type === 'PlayerReadyChanged') {
        return [];
    }
    const subs = [];
    const pushUnit = (instanceId, hookName, orderCell) => {
        const inst = draft.instances[instanceId];
        if (inst === undefined)
            return;
        const hooks = registry.get(inst.defId)?.hooks;
        if (!hookDefined(hooks, hookName))
            return;
        const cell = orderCell ?? inst.cellId;
        subs.push({
            subject: { selfId: instanceId },
            defId: inst.defId,
            hookName,
            ...(cell !== undefined ? { cellId: cell } : {}),
            seq: inst.seq,
        });
    };
    const pushEffect = (effect, hookName) => {
        const hooks = registry.get(effect.defId)?.hooks;
        if (!hookDefined(hooks, hookName))
            return;
        const cell = effectCellId(draft, effect);
        subs.push({
            subject: { effectId: effect.id },
            defId: effect.defId,
            hookName,
            ...(cell !== undefined ? { cellId: cell } : {}),
            seq: effect.seq,
        });
    };
    const pushAttachment = (attachmentId, hookName) => {
        const attachment = draft.attachments.find((candidate) => candidate.attachmentId === attachmentId);
        if (attachment === undefined)
            return;
        const hooks = registry.get(attachment.defId)?.hooks;
        if (!hookDefined(hooks, hookName))
            return;
        const cellId = draft.instances[attachment.hostInstanceId]?.cellId;
        subs.push({
            subject: { attachmentId: attachment.attachmentId },
            defId: attachment.defId,
            hookName,
            ...(cellId !== undefined ? { cellId } : {}),
            seq: attachment.seq,
        });
    };
    // onAnyEvent — каждое событие, для всех ЖИВЫХ экземпляров на поле.
    for (const inst of Object.values(draft.instances)) {
        if (!isLiveUnit(draft, inst))
            continue;
        const hooks = registry.get(inst.defId)?.hooks;
        if (hookDefined(hooks, 'onAnyEvent')) {
            subs.push({
                subject: { selfId: inst.id },
                defId: inst.defId,
                hookName: 'onAnyEvent',
                ...(inst.cellId !== undefined ? { cellId: inst.cellId } : {}),
                seq: inst.seq,
            });
        }
    }
    for (const effect of draft.effects)
        pushEffect(effect, 'onAnyEvent');
    for (const attachment of draft.attachments)
        pushAttachment(attachment.attachmentId, 'onAnyEvent');
    switch (event.type) {
        case 'UnitEnteredPlay':
            pushUnit(event.instanceId, 'onEnterPlay', event.cellId);
            break;
        case 'UnitDied':
            // Смерть: носитель мог покинуть ячейку — упорядочиваем по cellId из события.
            pushUnit(event.instanceId, 'onDeath', event.cellId);
            break;
        case 'DamageDealt':
            if (event.sourceId !== undefined)
                pushUnit(event.sourceId, 'onDealDamage');
            if (event.target.kind === 'unit')
                pushUnit(event.target.instanceId, 'onTakeDamage', event.targetCellId);
            break;
        case 'AttackPerformed':
            pushUnit(event.attackerId, 'onAttack');
            for (const effect of draft.effects)
                pushEffect(effect, 'onAttack');
            break;
        case 'AttackResolved':
            pushUnit(event.attackerId, 'onAttackResolved');
            for (const effect of draft.effects)
                pushEffect(effect, 'onAttackResolved');
            if (event.target.kind === 'unit') {
                pushUnit(event.target.instanceId, 'onAttacked', event.targetCellId);
            }
            break;
        case 'RoundStarted':
            for (const inst of Object.values(draft.instances)) {
                if (isLiveUnit(draft, inst))
                    pushUnit(inst.id, 'onRoundStart');
            }
            for (const effect of draft.effects)
                pushEffect(effect, 'onRoundStart');
            break;
        case 'RoundEnded':
            for (const inst of Object.values(draft.instances)) {
                if (isLiveUnit(draft, inst))
                    pushUnit(inst.id, 'onRoundEnd');
            }
            for (const effect of draft.effects)
                pushEffect(effect, 'onRoundEnd');
            break;
        default:
            break;
    }
    return subs;
}
/**
 * Единственный pre-reducer subscriber: обычный CardMoved board→zone должен
 * помнить старую cell placement для onLeave. У death-paired CardMoved cellId уже
 * очищен предшествующим UnitDied, поэтому onLeave корректно не возникает.
 */
export function collectPreApplySubscribers(draft, registry, event) {
    if (event.type !== 'CardMoved' || event.from !== 'board' || event.to === 'board')
        return [];
    const instance = draft.instances[event.instanceId];
    if (instance === undefined || instance.cellId === undefined || !isLiveUnit(draft, instance))
        return [];
    const hooks = registry.get(instance.defId)?.hooks;
    if (!hookDefined(hooks, 'onLeavePlay'))
        return [];
    return [{
            subject: { selfId: instance.id },
            defId: instance.defId,
            hookName: 'onLeavePlay',
            cellId: instance.cellId,
            seq: instance.seq,
        }];
}
/** Тело — генератор (yield ctx.choose)? Обычная функция возвращает void (undefined). */
export function isGenerator(body) {
    return (typeof body === 'object' &&
        body !== null &&
        typeof body.next === 'function');
}
/** Вызвать нужный хук с корректно суженным событием; ВЕРНУТЬ его тело (не драйвить). */
export function invokeHookBody(hooks, name, ctx, event) {
    switch (name) {
        case 'onAnyEvent':
            return hooks.onAnyEvent?.(ctx, event);
        case 'onEnterPlay':
            return event.type === 'UnitEnteredPlay' ? hooks.onEnterPlay?.(ctx, event) : undefined;
        case 'onLeavePlay':
            return hooks.onLeavePlay?.(ctx);
        case 'onDeath':
            return event.type === 'UnitDied' ? hooks.onDeath?.(ctx, event) : undefined;
        case 'onRoundStart':
            return hooks.onRoundStart?.(ctx);
        case 'onRoundEnd':
            return hooks.onRoundEnd?.(ctx);
        case 'onDealDamage':
            return event.type === 'DamageDealt' ? hooks.onDealDamage?.(ctx, event) : undefined;
        case 'onTakeDamage':
            return event.type === 'DamageDealt' ? hooks.onTakeDamage?.(ctx, event) : undefined;
        case 'onAttack':
            return event.type === 'AttackPerformed' ? hooks.onAttack?.(ctx, event) : undefined;
        case 'onAttackResolved':
            return event.type === 'AttackResolved' ? hooks.onAttackResolved?.(ctx, event) : undefined;
        case 'onAttacked':
            return event.type === 'AttackResolved' ? hooks.onAttacked?.(ctx, event) : undefined;
    }
}
//# sourceMappingURL=subscribers.js.map